/* @refresh reset */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ca from '../locales/ca.json';
import de from '../locales/de.json';
import en from '../locales/en.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';
import {
  detectLocale,
  persistLocale,
  type SupportedLocale,
} from '../locales/locale';

export type { SupportedLocale };

type TranslationMap = Record<string, string>;

interface LocaleContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: (key: string) => string;
}

const resources: Record<SupportedLocale, TranslationMap> = {
  en: en as TranslationMap,
  es: es as TranslationMap,
  fr: fr as TranslationMap,
  de: de as TranslationMap,
  ca: ca as TranslationMap,
};

export const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => detectLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: SupportedLocale) => {
    persistLocale(next);
    setLocaleState(next);
  }, []);

  const t = useMemo(() => {
    const map = resources[locale] || resources.en;
    return (key: string) => map[key] ?? resources.en[key] ?? key;
  }, [locale]);

  const value: LocaleContextValue = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = (): LocaleContextValue => {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return ctx;
};
